import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'hooks/redux.hooks';
import _, { sortBy } from 'lodash';
//mui
import { Box, Popover, Stack, Typography, useTheme } from '@mui/material';
import { BalancesBar } from 'theme/layouts/currencyBar';
import { getCompanyBalances } from 'state/reducers/balance.reducers';
import { getCurrencies } from 'state/reducers/global.reducers';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { AddButton } from 'theme/elements/Buttons';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { selectTranslations, selectLanguage } from 'state/reducers/translation.reducers';
import PendingBalanceTable from './PendingBalanceTable';
import { getOpenPendingRequests } from 'state/reducers/requests.reducers';
import { getActivities } from 'state/reducers/activity.reducers';
import { ActivityTypes } from 'constants/activity.constants';
import { StatusType, TransferType, amountType } from 'constants/balance.constants';
import { getUserPermission } from 'state/reducers/user.reducers';
import { UserPermission } from 'constants/user.constants';

export const formatNumber = (number?: number, type?: string) => {
  const shortNumber = number?.toFixed(2);
  const splitNumber = String(shortNumber).split('.');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline' }}>
      {type === amountType.FOR_USE && (
        <>
          {!number ? (
            <Typography variant={'h3_5'} fontFamily="RubikSemiBold">
              0
            </Typography>
          ) : (
            <Box dir="ltr" sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography variant="h3_5" fontFamily="RubikSemiBold">
                {parseInt(splitNumber[0]).toLocaleString()}
              </Typography>
              {splitNumber[1] !== '00' && (
                <Typography
                  variant="h3_5"
                  fontFamily="RubikSemiBold"
                  sx={{ fontSize: '14px!important' }}
                >
                  .{splitNumber[1]}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
      {type === amountType.PENDING && (
        <>
          {!number ? (
            <Typography variant="subtitle1" fontFamily="RubikSemiBold">
              0
            </Typography>
          ) : (
            <Box dir="ltr" sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography variant="subtitle1" fontFamily="RubikSemiBold">
                {parseInt(splitNumber[0]).toLocaleString()}
              </Typography>
              {splitNumber[1] !== '00' && (
                <Typography variant="subtitle1" fontFamily="RubikSemiBold" sx={{ fontSize: '12px' }}>
                  .{splitNumber[1]}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const renderHTML = (rawHTML: string) => React.createElement('span', { dangerouslySetInnerHTML: { __html: rawHTML } });

export default function BalancesGallery() {
  const t = useAppSelector(selectTranslations);
  const theme = useTheme();
  const navigate = useNavigate();
  const companyBalancesList = useAppSelector(getCompanyBalances);
  const sortedCompanyBalancesList = sortBy(companyBalancesList, 'amount').reverse();
  const currencies = useAppSelector(getCurrencies);
  const [page, setPage] = useState(0);
  const lang = useAppSelector(selectLanguage);
  const cardwidth = 220;
  const cardHeight = '120px';
  const gap = 3 * 8;
  const itemsPerPage = 4;
  const galleryWidth = itemsPerPage * cardwidth + gap * (itemsPerPage - 1);
  const addNewLeft =
    companyBalancesList.length <= itemsPerPage
      ? companyBalancesList.length * (cardwidth + gap)
      : itemsPerPage * (cardwidth + gap) + gap;
  const { balancesRequests } = useAppSelector(getOpenPendingRequests);
  const companyActivities = useAppSelector(getActivities);
  const withdrawalsRequests = useAppSelector((state => state.pendingRequests.withdrawalRequests))
  const [pendingDataToShow, setPendingDataToShow] = useState<any>(null);
  const [pendingCurrency, setPendingCurrency] = useState('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const userPermission = useAppSelector(getUserPermission);
  const handleClose = () => {
    setAnchorEl(null);
    setPendingDataToShow(null);
    setPendingCurrency('');
  };

  const isBalanceAddShow = useMemo(
    () => [UserPermission.OPERATIONER, UserPermission.SIGNER].includes(userPermission),
    [userPermission]
  );

  const handlePendingAmountHover = (data: any, e: any) => {
    if (data.pendingAmount) {
      let balancesResults: any = [];
      let companyActivitiesResults: any = [];
      let companyWithdrawalsActivities: any = []
      let companyWithdrawalsRequests: any = []

      if (balancesRequests) {
        balancesResults = balancesRequests?.filter(
          (balanceRequest: any) =>
            balanceRequest.currency?.id === data.currencyId &&
            balanceRequest.type === TransferType.Topping &&
            (balanceRequest.status === StatusType.Pending ||
              balanceRequest.status === StatusType.PendingBoSignerReview)
        );
      }
      if (companyActivities) {
        companyActivitiesResults = companyActivities.reduce(
          (total: any, activity: any) =>
            activity.pendingQuote?.buyCurrency?.id === data.currencyId &&
              activity.type === ActivityTypes.conversionActivities &&
              (activity.status === StatusType.Pending || activity.status === StatusType.PendingScheduler)
              ? [
                ...total,
                {
                  referenceNumber: activity.reference,
                  transactionType: activity.transactionType,
                  amount: activity.pendingQuote?.altshulerBuyAmount,
                  date: activity.createdAt,
                },
              ]
              : total,
          []
        );
      }
      if (companyActivities) {
        companyWithdrawalsRequests = companyActivities.reduce(
          (total: any, activity: any) =>
            activity?.currency?.id === data.currencyId &&
              activity.balanceType === "Withdrawal" &&
              (activity.status === StatusType.PendingBoSignerReview)
              ? [
                ...total,
                {
                  referenceNumber: activity.reference,
                  transactionType: 'Withdrawal',
                  amount: activity.amount,
                  date: activity.createdAt,
                },
              ]
              : total,
          []
        );
      }
      if (withdrawalsRequests) {
        companyWithdrawalsActivities = withdrawalsRequests.reduce(
          (total: any, activity: any) =>
            activity?.currency === data?.currency?.code &&
              (activity.status === StatusType.Pending)
              ? [
                ...total,
                {
                  referenceNumber: activity.reference,
                  transactionType: 'Withdrawal',
                  amount: activity.amount,
                  date: activity.createdAt,
                },
              ]
              : total,
          []
        );
      }

      setPendingDataToShow([
        ...balancesResults,
        ...companyActivitiesResults,
        ...companyWithdrawalsActivities,
        ...companyWithdrawalsRequests,
      ]);
      setPendingCurrency(data.currency.symbol);
      setAnchorEl(e);
    }
  };

  return (
    <Box display={{ xs: 'none', sm: 'block' }} sx={{ position: 'relative' }} width={'100%'}>
      <Box sx={{ display: 'flex', gap: '20px' }}>
        {companyBalancesList.length > 0 && page > 0 && (
          <ArrowBackIosIcon
            onClick={() => setPage(page - 1)}
            sx={{
              zIndex: 100,
              cursor: 'pointer',
              position: 'absolute',
              top: 0,
              left: '-18px',
              height: cardHeight,
              transform: lang == 'he' ? 'rotate(180deg)' : '',
            }}
          />
        )}
        <Box
          sx={{
            height: cardHeight,
            width: galleryWidth + 'px',
            maxWidth: galleryWidth + 'px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <BalancesBar
            gap={3}
            display={{ xs: 'none', sm: 'flex' }}
            sx={{
              left: 0 - page * (galleryWidth + gap),
              height: cardHeight,
            }}
          >
            {currencies.length > 0 &&
              sortedCompanyBalancesList.map((currency, index) => (
                <Box
                  key={index}
                  sx={{
                    minWidth: '220px',
                    minHeight: cardHeight,
                    backgroundColor: `rgba(0,71,81,${index % itemsPerPage > 0 ? '0.' + (10 - (index % itemsPerPage)) : 1
                      })`,
                    color: '#fff',
                    borderRadius: '15px',
                    padding: '7px 14px 12px 14px',
                    boxShadow: '0 2px 4px 0 rgba(20, 96, 84, 0.2)',
                  }}
                >
                  <Typography variant="h3" sx={{ lineHeight: 'initial' }}>
                    {renderHTML(_.get(_.keyBy(currencies, 'id'), currency?.currencyId)?.symbol)}
                  </Typography>
                  <Typography variant="captionRegular">
                    {renderHTML(_.get(_.keyBy(currencies, 'id'), currency?.currencyId)?.code)}
                  </Typography>
                  <Stack spacing={0.5}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <Typography
                        variant="captionRegular"
                        sx={{ marginRight: '10px', textWrap: 'nowrap' }}
                      >
                        {t('page.dashboard.balances.available.text')}
                      </Typography>
                      {typeof currency?.amount !== 'string'
                        ? formatNumber(currency?.amount, amountType.FOR_USE)
                        : currency?.amount}
                    </Box>
                    <Box
                      sx={{ border: `0.5px solid ${theme.palette.root.line}`, opacity: '0.2' }}
                    ></Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        position: 'relative',
                      }}
                      onMouseEnter={_.debounce(
                        (e) => {
                          handlePendingAmountHover(currency, e.target);
                        },
                        300,
                        { maxWait: 300 }
                      )}
                      onMouseLeave={_.debounce(handleClose, 300)}
                    >
                      <Typography variant="captionRegular" sx={{ marginRight: '10px' }}>
                        {t('page.dashboard.balances.pending.text')}
                      </Typography>
                      {typeof currency?.amount !== 'string'
                        ? formatNumber(currency?.pendingAmount, amountType.PENDING)
                        : currency?.amount}
                    </Box>
                  </Stack>
                </Box>
              ))}
          </BalancesBar>
        </Box>
        {companyBalancesList.length > 0 && itemsPerPage * (page + 1) < companyBalancesList.length && (
          <ArrowForwardIosIcon
            onClick={() => setPage(page + 1)}
            sx={{
              cursor: 'pointer',
              position: 'absolute',
              top: 0,
              left: galleryWidth + 'px',
              height: cardHeight,
              transform: lang == 'he' ? 'rotate(180deg)' : '',
            }}
          />
        )}
        {isBalanceAddShow && (
          <Stack
            sx={{
              cursor: 'pointer',
              height: cardHeight,
              textAlign: 'center',
            }}
          >
            <AddButton size={90} onClick={() => navigate('/' + pagesEnum.BALANCE)} />
            <Typography>{t('page.dashboard.add_balance_button.text')}</Typography>
          </Stack>
        )}
      </Box>
      <Popover
        style={{ pointerEvents: 'none' }}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <PendingBalanceTable currencyId={pendingCurrency} pendingDataToShow={pendingDataToShow} />
      </Popover>
    </Box>
  );
}
